import { useEffect, useRef, useState } from "react";

/**
 * LazyMount — defers rendering of its children until the wrapper scrolls into
 * the viewport (with a generous `rootMargin` so content is ready a screen-
 * worth before the user actually sees it).
 *
 * Why this exists
 * ---------------
 * Several below-the-fold homepage sections (Pinterest trending pins, the
 * testimonials carousel that pulls /api/testimonials, the FAQ accordion with
 * its JSON-LD schema, etc.) are expensive to mount yet invisible during the
 * critical paint. Lighthouse's desktop run STILL counts their script-eval
 * cost against TBT because the run loads at 1440×900 viewport with no scroll.
 *
 * By gating mount behind `IntersectionObserver` we keep them effectively
 * "off" until the user begins scrolling — but they hydrate well before the
 * user actually reaches them (default `rootMargin: 600px 0px`). The end-user
 * experience is identical to eager mounting; PSI sees a smaller initial JS
 * execution window, which lowers TBT and long-tasks count.
 *
 * Props
 * -----
 *  • children          — the component(s) to mount lazily.
 *  • rootMargin        — IntersectionObserver root margin. Default `600px 0px`
 *                        renders content well before it enters the viewport
 *                        on fast desktops, avoiding any perceptible pop-in.
 *  • minHeight         — placeholder height while idle, to keep CLS at 0.
 *                        Caller knows the section's approximate height; pass
 *                        the closest sensible value (px or any CSS length).
 *  • testId            — optional data-testid forwarded to the placeholder.
 *
 * If `IntersectionObserver` is unavailable (very old browser or SSR/prerender)
 * we fall back to rendering immediately — graceful degradation rather than a
 * permanently blank section.
 */
export default function LazyMount({
    children,
    rootMargin = "600px 0px",
    minHeight = "400px",
    testId,
}) {
    const ref = useRef(null);
    const [mounted, setMounted] = useState(() => {
        // SSR / prerender / very old browsers — render immediately so crawlers
        // and `prerender.js` see the markup, and so screen readers don't see
        // an empty region.
        if (typeof window === "undefined") return true;
        if (typeof window.IntersectionObserver !== "function") return true;
        return false;
    });

    useEffect(() => {
        if (mounted) return undefined;
        if (!ref.current) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setMounted(true);
                    io.disconnect();
                }
            },
            { rootMargin },
        );
        io.observe(ref.current);
        return () => io.disconnect();
    }, [mounted, rootMargin]);

    if (mounted) return children;
    return (
        <div
            ref={ref}
            style={{ minHeight }}
            aria-hidden="true"
            data-testid={testId || "lazy-mount-placeholder"}
        />
    );
}
